import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import { API, getAuthHeader } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertTriangle, CheckCircle, Clock, FileText, Bike, Shield, Filter, Plus, Search, Edit, Save, X, MoreVertical, Eye } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import InsuranceClaims from '../components/InsuranceClaims';
import { SortButton, sortData, DateRangeFilter, SearchFilters, filterData } from '../components/DataTableWithFilters';
import { formatDateDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';

const Insurance = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  const [activeTab, setActiveTab] = useState('policies'); // 'policies' or 'claims'
  const [insuranceData, setInsuranceData] = useState([]);
  const [motorcycles, setMotorcycles] = useState([]);
  const [riders, setRiders] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ field: 'start_date', direction: 'desc' });
  
  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ policy_number: '', premium_amount: '' });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBike, setSelectedBike] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all'); // For stat card highlighting only
  
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    lessee: '',
    rider: '',
    bike: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    expired: 0,
    expiringSoon: 0,
    active: 0
  });

  useEffect(() => {
    fetchInsuranceData();
  }, []);

  // Inline editing functions
  const handleStartEdit = (record) => {
    setEditingId(record.id);
    setEditValues({
      policy_number: record.policy_number || '',
      premium_amount: record.premium_amount || 0
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({ policy_number: '', premium_amount: '' });
  };

  const handleSaveEdit = async (record) => {
    try {
      // Use PATCH for partial update of insurance info
      await axios.patch(`${API}/motorcycles/${record.id}`, {
        insurance_policy_number: editValues.policy_number,
        insurance_premium_amount: parseFloat(editValues.premium_amount) || 0
      }, { headers: getAuthHeader() });
      
      toast.success('Insurance details updated successfully');
      setEditingId(null);
      fetchInsuranceData(); // Refresh data
    } catch (error) {
      toast.error('Failed to update insurance details');
      console.error(error);
    }
  };

  // Apply filters and sorting
  const normalizedInsurance = insuranceData.map(record => ({
    ...record,
    date: record.insurance_start_date // Normalize date field for filtering
  }));
  
  // Apply advanced filters from the filter UI
  let filteredData = filterData(normalizedInsurance, filters);
  
  // Apply stat card filter (for expired/expiring/active clicks)
  if (activeFilter === 'expired') {
    filteredData = filteredData.filter(d => d.is_expired);
  } else if (activeFilter === 'expiring') {
    filteredData = filteredData.filter(d => d.is_expiring_soon && !d.is_expired);
  } else if (activeFilter === 'active') {
    filteredData = filteredData.filter(d => !d.is_expired && !d.is_expiring_soon);
  }
  // 'all' - no additional filtering
  
  const sortedData = sortData(filteredData, sortConfig.field, sortConfig.direction);

  const handleSort = (field, direction) => {
    setSortConfig({ field, direction });
  };

  const fetchInsuranceData = async () => {
    try {
      const [insuranceRes, bikesRes, ridersRes, lesseesRes] = await Promise.all([
        axios.get(`${API}/insurance/from-motorcycles`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() })
      ]);
      
      const data = insuranceRes.data;
      
      setInsuranceData(data);
      setMotorcycles(bikesRes.data);
      setRiders(ridersRes.data);
      setLessees(lesseesRes.data);
      
      // Calculate stats
      const expired = data.filter(d => d.is_expired).length;
      const expiringSoon = data.filter(d => d.is_expiring_soon && !d.is_expired).length;
      const active = data.filter(d => !d.is_expired && !d.is_expiring_soon).length;
      
      setStats({
        total: data.length,
        expired,
        expiringSoon,
        active
      });
    } catch (error) {
      toast.error('Failed to fetch insurance data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    // Note: The actual filtering is handled by the computed sortedData
    // This is just for visual feedback on the stat cards
  };

  const handleViewDetails = async (record) => {
    try {
      // Fetch full motorcycle details
      const response = await axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() });
      const bike = response.data.find(m => m.plate_number === record.plate_number);
      
      // Get rider name from riders list (find rider assigned to this bike)
      const assignedRider = riders.find(r => r.assigned_bike_id === bike?.id);
      
      // Get lessee name from lessees list
      const lessee = lessees.find(l => l.id === bike?.lessee_id);
      
      // Merge bike data with rider and lessee info
      const enrichedBike = {
        ...bike,
        rider_name: bike?.lessee_name || assignedRider?.name || record.rider_name || '',
        lessee_company_name: lessee?.company_name || record.lessee_name || ''
      };
      
      setSelectedBike(enrichedBike);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch motorcycle details');
    }
  };

  const getStatusBadge = (record) => {
    if (record.is_expired) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertTriangle className="w-3 h-3" />
          Expired
        </Badge>
      );
    } else if (record.is_expiring_soon) {
      return (
        <Badge className="bg-orange-500 hover:bg-orange-600 gap-1">
          <Clock className="w-3 h-3" />
          Expiring in {record.days_until_expiry} days
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-green-500 hover:bg-green-600 gap-1">
          <CheckCircle className="w-3 h-3" />
          Active ({record.days_until_expiry} days left)
        </Badge>
      );
    }
  };

  const getInsuranceStatus = (record) => {
    if (record.is_expired) {
      return 'Expired';
    } else if (record.is_expiring_soon) {
      return `Expiring in ${record.days_until_expiry} days`;
    } else {
      return `Active (${record.days_until_expiry} days left)`;
    }
  };

  const getInsuranceStatusColor = (record) => {
    if (record.is_expired) {
      return 'bg-red-100 text-red-800';
    } else if (record.is_expiring_soon) {
      return 'bg-orange-100 text-orange-800';
    } else {
      return 'bg-green-100 text-green-800';
    }
  };

  const getRowClassName = (record) => {
    if (record.is_expired) {
      return 'bg-red-50 hover:bg-red-100';
    } else if (record.is_expiring_soon) {
      return 'bg-orange-50 hover:bg-orange-100';
    }
    return '';
  };

  if (loading && activeTab === 'policies') return <div className="flex items-center justify-center h-96">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="insurance-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-800">{t('insurance.title')}</h1>
        <p className="text-slate-600 mt-1">{t('insurance.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'policies'
              ? 'text-lime-600 border-b-2 border-lime-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
          onClick={() => setActiveTab('policies')}
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Insurance Policies
          </div>
        </button>
        <button
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'claims'
              ? 'text-lime-600 border-b-2 border-lime-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
          onClick={() => setActiveTab('claims')}
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Insurance Claims
          </div>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'claims' ? (
        <InsuranceClaims />
      ) : (
        <>
          {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className={`modern-card stat-card cursor-pointer transition-all ${activeFilter === 'all' ? 'ring-2 ring-lime-500' : ''}`}
          onClick={() => handleFilterClick('all')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 uppercase">Total Motorcycles</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-lime-600 to-green-600 bg-clip-text text-transparent">{stats.total}</p>
              </div>
              <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-500 to-slate-600 shadow-lg">
                <FileText className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`modern-card stat-card cursor-pointer transition-all ${activeFilter === 'expired' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => handleFilterClick('expired')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 uppercase">Expired</p>
                <p className="text-3xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`modern-card stat-card cursor-pointer transition-all ${activeFilter === 'expiring' ? 'ring-2 ring-orange-500' : ''}`}
          onClick={() => handleFilterClick('expiring')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 uppercase">Expiring Soon</p>
                <p className="text-3xl font-bold text-orange-600">{stats.expiringSoon}</p>
              </div>
              <div className="p-4 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg">
                <Clock className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`modern-card stat-card cursor-pointer transition-all ${activeFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => handleFilterClick('active')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 uppercase">Active</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="p-4 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 shadow-lg">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insurance Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Insurance Expiry Tracker ({sortedData.length})</CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                Motorcycles expiring within 30 days are highlighted. Data is automatically extracted from motorcycle records.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showFilters && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg space-y-4">
              <DateRangeFilter
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                onFromDateChange={(val) => setFilters({ ...filters, fromDate: val })}
                onToDateChange={(val) => setFilters({ ...filters, toDate: val })}
              />
              <SearchFilters
                lessees={lessees}
                riders={riders}
                motorcycles={motorcycles}
                selectedLessee={filters.lessee}
                selectedRider={filters.rider}
                selectedBike={filters.bike}
                onLesseeChange={(val) => setFilters({ ...filters, lessee: val })}
                onRiderChange={(val) => setFilters({ ...filters, rider: val })}
                onBikeChange={(val) => setFilters({ ...filters, bike: val })}
              />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setFilters({ fromDate: '', toDate: '', lessee: '', rider: '', bike: '' })}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 border-b border-slate-200">
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[130px]">
                    <div className="flex items-center gap-1">
                      Period
                      <SortButton onSort={(field, dir) => setSortConfig({field, direction: dir})} currentSort={sortConfig} field="start_date" />
                    </div>
                  </TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[100px]">Plate No.</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[180px]">Lessee Company</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[140px]">Rider Name</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[140px]">Provider</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[110px]">Policy No.</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[100px] text-right">Premium</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[120px] text-center">Status</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-xs uppercase tracking-wider px-4 py-3 w-[80px] text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-slate-500 py-12">
                      <div className="space-y-2">
                        <Shield className="w-8 h-8 mx-auto text-slate-400" />
                        <div className="text-sm">No insurance records found. Add insurance information to motorcycles to see them here.</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((record) => (
                    <TableRow 
                      key={record.id}
                      className={`hover:bg-orange-50 transition-colors duration-150 border-b border-slate-100 ${
                        record.is_expired ? 'bg-red-50' : record.is_expiring_soon ? 'bg-amber-50' : ''
                      }`}
                    >
                      <TableCell className="px-4 py-3 text-sm text-slate-700">
                        <div className="whitespace-nowrap">
                          <div>{record.start_date ? formatDateDisplay(record.start_date) : '-'}</div>
                          <div className="text-slate-400">to {record.end_date ? formatDateDisplay(record.end_date) : '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 font-medium whitespace-nowrap">{record.plate_number || '-'}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 truncate max-w-[180px]" title={record.lessee_name}>{record.lessee_name || '-'}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 truncate max-w-[140px]" title={record.rider_name}>{record.rider_name || '-'}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700 truncate max-w-[140px]" title={record.insurance_provider}>{record.insurance_provider || '-'}</TableCell>
                      <TableCell className="px-4 py-3 text-sm text-slate-700">
                        {editingId === record.id ? (
                          <Input
                            value={editValues.policy_number}
                            onChange={(e) => setEditValues({ ...editValues, policy_number: e.target.value })}
                            className="h-8 w-full text-sm"
                            placeholder="Policy #"
                          />
                        ) : (
                          <span className="whitespace-nowrap">{record.policy_number || '-'}</span>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-sm text-right">
                        {editingId === record.id ? (
                          <Input
                            type="number"
                            value={editValues.premium_amount}
                            onChange={(e) => setEditValues({ ...editValues, premium_amount: e.target.value })}
                            className="h-8 w-full text-sm text-right"
                            placeholder="0.00"
                          />
                        ) : (
                          <span className="font-semibold text-slate-700 whitespace-nowrap"><CurrencyDisplay amount={record.premium_amount || 0} /></span>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        <Badge className={`text-xs px-2 py-1 ${getInsuranceStatusColor(record)}`}>
                          {getInsuranceStatus(record)}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex gap-1 justify-center">
                          {editingId === record.id ? (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleSaveEdit(record)}
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleStartEdit(record)}
                                title="Edit Policy & Premium"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-slate-600 hover:text-slate-700 hover:bg-slate-100"
                                onClick={() => handleViewDetails(record)}
                                title="View Details"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="text-sm font-medium text-slate-700">Legend:</div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-50 border border-red-200 rounded"></div>
              <span className="text-sm text-slate-600">Expired Insurance</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-orange-50 border border-orange-200 rounded"></div>
              <span className="text-sm text-slate-600">Expiring within 30 days</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-white border border-slate-200 rounded"></div>
              <span className="text-sm text-slate-600">Active Insurance</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Motorcycle Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-3xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Bike className="w-6 h-6 text-lime-600" />
              Motorcycle Details
            </DialogTitle>
            <DialogDescription>
              Complete information for {selectedBike?.plate_number}
            </DialogDescription>
          </DialogHeader>

          {selectedBike && (
            <div className="space-y-6 mt-4">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Plate Number</label>
                  <p className="text-lg font-semibold text-slate-900">{selectedBike.plate_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Chassis Number</label>
                  <p className="text-lg font-mono text-slate-900">{selectedBike.chassis_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Model</label>
                  <p className="text-lg text-slate-900">{selectedBike.model_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Model Version</label>
                  <p className="text-lg text-slate-900">{selectedBike.model_version || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Color</label>
                  <p className="text-lg text-slate-900">{selectedBike.color}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Status</label>
                  <Badge className="mt-1">{selectedBike.status}</Badge>
                </div>
              </div>

              {/* Insurance Information */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Insurance Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Insurance Provider</label>
                    <p className="text-lg text-slate-900">{selectedBike.insurance_provider || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Policy Number</label>
                    <p className="text-lg text-slate-900">{selectedBike.insurance_policy_number || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Start Date</label>
                    <p className="text-lg text-slate-900">{selectedBike.insurance_start_date ? formatDateDisplay(selectedBike.insurance_start_date) : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Expiry Date</label>
                    <p className="text-lg font-semibold text-slate-900">{selectedBike.insurance_expiry_date || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Additional Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">City</label>
                    <p className="text-lg text-slate-900">{selectedBike.city || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Salik Tag No.</label>
                    <p className="text-lg text-slate-900">{selectedBike.salik_tag_no || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Lessee Company</label>
                    <p className="text-lg text-slate-900">{selectedBike.lessee_company_name || selectedBike.lessee_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Rider</label>
                    <p className="text-lg text-slate-900">{selectedBike.rider_name || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setDetailDialogOpen(false)}
                className="w-full bg-lime-600 hover:bg-lime-700"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default Insurance;